/**
 * usePilotFooter hook.
 */
import EpgEvent from '@shared/classes/epgEvent';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { DELAY } from '@shared/types/constants/animations';
import { PilotProps as Props } from '@shared/types/props/pilot';
import { useMemo } from 'react';

/**
 * Hook to get the pilot footer content data.
 */
export default function usePilotFooter({
  asset,
  channel,
  hint,
  display: displayPilot,
  type,
}: Props) {
  // Destructure event
  const {
    ageCode,
    eventId,
    poster,
    score: rating,
    title: eventTitle,
  } = asset || {};

  // Destructure channel
  const {
    id: channelId,
    logo: channelLogo,
    title: channelTitle,
  } = channel || {};

  // Memoize the pilot event title
  const pilotEventTitle = useMemo(() => {
    const { episodeNumber, seasonNumber, episodeTitle } = asset || {};
    const literals = {
      episode: episodeNumber
        ? `${TranslationManager.get(
            'MISC.EVENTS.EPISODE_FIRST_LETTER'
          )}${episodeNumber}`
        : '',
      season: seasonNumber
        ? `${TranslationManager.get(
            'MISC.EVENTS.SEASON_FIRST_LETTER'
          )}${seasonNumber}`
        : '',
      title: episodeTitle ? `${episodeTitle}` : '',
    };
    const separator = {
      initial: ' · ',
      slash: ' | ',
    };
    const titleInfo = [...Object.values(literals)]
      .filter((item) => item.length > 0)
      .join(separator.slash);
    return `${eventTitle ?? ''}${
      titleInfo ? separator.initial : ''
    }${titleInfo}`;
  }, [eventTitle]);

  // Memoize and return the pilot content data
  return useMemo(() => {
    return {
      props: {
        content: {
          delay: DELAY.PILOT_FOOTER_CONTENT,
        },
        event: {
          id: channelId,
          logo: channelLogo,
          subtitle: pilotEventTitle,
          title: channelTitle,
          type,
        },
        hint: {
          button: {
            action: () => hint?.action?.(),
            text: TranslationManager.get('No mostrar más'),
          },
          text: {
            left: TranslationManager.get('Pulsa botón derecho'),
            right: TranslationManager.get('para acceder a la lista de canales'),
          },
        },
        info: {
          ageCode,
          rating,
        },
        poster: {
          id: eventId,
          poster,
          lowPoster: (asset as EpgEvent)?.lowPoster || '',
        },
        progressBar: {
          event: asset,
          type,
        },
      },
    };
  }, [asset, channel, displayPilot, hint, type, pilotEventTitle]);
}
